import GlowBar from 'components/GlowBar';
import MainLayout from 'components/layout/MainLayout';
import PageTitle from 'components/PageTitle';
import { NextSeo } from 'next-seo';
import Head from 'next/head';
import css from 'styled-jsx/css';

const PrivacyPolicy = () => {
  return (
    <MainLayout>
      <Head>
        <meta name="robots" content="noindex,nofollow" />
      </Head>
      <NextSeo
        title="Privacy Policy | Win Epic Prizes | DAYMADE"
        openGraph={{
          title: 'Privacy Policy | Win Epic Prizes | DAYMADE',
        }}
      />

      <PageTitle>
        Privacy <strong>Policy</strong>
      </PageTitle>

      <div className="paddedMaxWidthContainer" style={{ padding: '2em' }}>
        <div className="container">
          <h2>INTRODUCTION</h2>
          <p>
            DAYMADE is an associated brand of, and operated by, DAYMADE LTD
            (registered in England & Wales, company number 12333770).
          </p>
          <p>
            DAYMADE LTD respects your privacy and is committed to protecting
            your personal data. This privacy policy will inform you as to how we
            look after your personal data when you visit our website (regardless
            of where you visit it from) and tell you about your privacy rights
            and how the law protects you.
          </p>

          <h2>Contents</h2>
          <ol>
            <li>IMPORTANT INFORMATION AND WHO WE ARE</li>
            <li>THE DATA WE COLLECT ABOUT YOU</li>
            <li>HOW AND WHY WE USE YOUR PERSONAL DATA</li>
            <li>COOKIES</li>
            <li>DISCLOSURES OF YOUR PERSONAL DATA TO THIRD PARTIES</li>
            <li>INTERNATIONAL TRANSFERS</li>
            <li>DATA SECURITY</li>
            <li>DATA RETENTION</li>
            <li>YOUR LEGAL RIGHTS</li>
            <li>COMPLAINTS</li>
            <li>CHANGES TO THIS PRIVACY POLICY</li>
          </ol>

          <h3>1. IMPORTANT INFORMATION AND WHO WE ARE</h3>
          <h4>PURPOSE OF THIS PRIVACY POLICY</h4>
          <p>
            This privacy policy aims to give you information on how DAYMADE LTD
            collects and processes your personal data through your use of this
            website, including any data you may provide through this website
            when you sign up to our newsletter.
          </p>
          <p>
            This website is not intended for children and we do not knowingly
            collect data relating to children.
          </p>
          <p>
            It is important that you read this privacy policy together with any
            other privacy policy or fair processing policy we may provide on
            specific occasions when we are collecting or processing personal
            data about you so that you are fully aware of how and why we are
            using your data. This privacy policy supplements other notices and
            privacy policies and is not intended to override them.
          </p>
          <h4>CONTROLLER</h4>
          <p>
            DAYMADE LTD is the controller and responsible for your personal
            data, "we", "us" or "our" in this privacy policy.
          </p>
          <p>
            We have appointed a data protection officer (DPO) who is responsible
            for overseeing questions in relation to this privacy policy. If you
            have any questions about this privacy policy, including any requests
            to exercise, please contact the DPO using the details set out below.
          </p>
          <h4>CONTACT DETAILS</h4>
          <p>
            If you have any questions about this privacy policy or our privacy
            practices, please contact our DPO in the following ways:
          </p>
          <p>Full name of legal entity: DAYMADE LTD</p>
          <p>Email address: lisa@daymade.co.uk</p>
          <p>Postal address: DAYMADE LTD, 66 Paul Street, London, EC2A 4NA</p>
          <p>
            You have the right to make a complaint at any time to the
            Information Commissioner’s Office (ICO), the UK supervisory
            authority for data protection issues (
            <a target="_blank" href="http://www.ico.org.uk/" rel="noreferrer">
              www.ico.org.uk
            </a>
            ). We would, however, appreciate the chance to deal with your
            concerns before you approach the ICO so please contact us in the
            first instance.
          </p>
          <h4>YOUR DUTY TO INFORM US OF CHANGES</h4>
          <p>
            It is important that the personal data we hold about you is accurate
            and current. Please keep us informed if your personal data changes
            during your relationship with us.
          </p>
          <h4>THIRD-PARTY LINKS</h4>
          <p>
            This website may include links to third-party websites, plug-ins and
            applications. Clicking on those links or enabling those connections
            may allow third parties to collect or share data about you. We do
            not control these third-party websites and are not responsible for
            their privacy statements. When you leave our website, we encourage
            you to read the privacy policy of every website you visit.
          </p>

          <h3>2. THE DATA WE COLLECT ABOUT YOU</h3>
          <p>
            Personal data, or personal information, means any information about
            an individual from which that person can be identified. It does not
            include data where the identity has been removed (anonymous data).
          </p>
          <p>
            We may collect, use, store and transfer different kinds of personal
            data about you which we have grouped together as follows:
          </p>
          <ol>
            <li>
              Personal and contact information when you register your details at
              DAYMADE online (or update that information in your account),
              including your name, email address, phone number, date of birth,
              and gender.
            </li>
            <li>
              Details of any conversations that you have with our customer
              support team or otherwise, including by email, telephone (voice
              recordings), post and online via webchat, the feedback button or
              on our social media channels.
            </li>
            <li>
              Copies of documents you provide to prove your identity (including
              driving licence and passport) when you are claiming a prize, or
              where there is a legal reason to request this from you.
            </li>
            <li>
              Transaction details including payments from you for DAYMADE prize
              draw entries, payments to you (for example prizes) and details of
              which prize draws you have entered and the outcome of those
              entries.
            </li>
            <li>
              Payment card information when you make a purchase. Note that your
              full payment card details are never stored on our servers but only
              with our fully PCI compliant payment processor.
            </li>
            <li>
              Personal and contact information, including your image, voice,
              footage (and any other information that identifies you) when
              contributing to our advertising and/or media content, assuming you
              have signed an appropriate consent/release form.
            </li>
            <li>
              Information about how you use and interact with our website, and
              which website(s) you came to the DAYMADE website from.
            </li>
            <li>
              Technical information about your device or browser when you use
              our website, including geolocation data to determine what country
              you are accessing our website from, your internet protocol (IP)
              address, device ID, browser type and version and time zone
              setting, which may in some circumstances be personal data.
            </li>
            <li>
              We may also receive personal data directly from you or from
              various third parties and public sources, such as social media
              channels (if you interact with us through those channels), or
              other third parties used to verify age/identity.
            </li>
          </ol>
          <p>
            We do not collect any Special Categories of Personal Data about you
            (this includes details about your race or ethnicity, religious or
            philosophical beliefs, sex life, sexual orientation, political
            opinions, trade union membership, information about your health, and
            genetic and biometric data). We also do not collect any information
            about criminal convictions and offences.
          </p>
          <h4>IF YOU FAIL TO PROVIDE PERSONAL DATA</h4>
          <p>
            Where we need to collect personal data by law, or under the terms of
            a contract we have with you, and you fail to provide that data when
            requested, we may not be able to perform the contract we have or are
            trying to enter into with you (for example, to provide you with
            goods or services). In this case, we may have to cancel a product or
            service you have with us but we will notify you if this is the case
            at the time.
          </p>

          <h3>3. HOW AND WHY WE USE YOUR PERSONAL DATA</h3>
          <p>
            We will only use your personal data when the law allows us to.
            Generally, we do not rely on consent as a legal basis for processing
            your personal data although we will get your consent before sending
            third party direct marketing communications to you via email or text
            message. You have the right to withdraw consent to marketing at any
            time by contacting us.
          </p>
          <h4>PURPOSES FOR WHICH WE WILL USE YOUR PERSONAL DATA</h4>
          <p>
            We have set out below, in a table format, a description of all the
            ways we plan to use your personal data, and which of the legal bases
            we rely on to do so. We have also identified what our legitimate
            interests are where appropriate.
          </p>
          <p>
            Note that we may process your personal data for more than one lawful
            ground depending on the specific purpose for which we are using your
            data. Please contact us if you need details about the specific legal
            ground we are relying on to process your personal data where more
            than one ground has been set out in the table below.
          </p>
          <table cellSpacing="0" cellPadding="0">
            <thead>
              <tr>
                <th>How we use your personal data</th>
                <th>Why we use your personal data</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>
                  <p>
                    We use your personal and contact information to register you
                    as a new customer.
                  </p>
                  <p>
                    We collect financial information if you register payment
                    card information to your account or set up entering by
                    subscription.
                  </p>
                </td>
                <td>Performance of a contract with you.</td>
              </tr>
              <tr>
                <td>
                  <p>
                    We use technical information such as the location or time
                    zone from which you access our website, the personal details
                    you provide at account set up and, where relevant, any
                    identity documents you have provided to check whether you
                    are eligible to enter our prize draws.
                  </p>
                  <p>
                    When you set up your account we may use an automated process
                    to verify that the details you provide are correct and to
                    verify you are over 18, and that you are a UK resident.
                  </p>
                </td>
                <td>Necessary to comply with a legal obligation.</td>
              </tr>
              <tr>
                <td>
                  We may require copies of documents to verify your identity
                  where we are required by law to provide assistance or in order
                  to comply with any request you may make regarding a DAYMADE
                  account.
                </td>
                <td>Necessary to comply with a legal obligation.</td>
              </tr>
              <tr>
                <td>
                  We use the personal and contact information provided by you
                  and the transaction details about your purchases, to provide
                  our games and other services to you.
                </td>
                <td>Performance of a contract with you.</td>
              </tr>
              <tr>
                <td>
                  We use your personal and contact information, financial
                  information, and transaction details about your DAYMADE
                  purchases, to manage your DAYMADE account, to contact you if
                  you’ve won a prize, and to pay those prizes.
                </td>
                <td>Performance of a contract with you.</td>
              </tr>
              <tr>
                <td>
                  We use your personal and contact information to contact you if
                  you’ve won a prize, and to pay (or facilitate payment of)
                  those prizes.
                </td>
                <td>Performance of a contract with you.</td>
              </tr>
              <tr>
                <td>
                  We use the personal and contact information provided by you,
                  copies of your communications with us and, in some
                  circumstances, transaction details, to manage our relationship
                  with you.
                </td>
                <td>
                  <p>Performance of a contract with you.</p>
                  <p>Necessary to comply with a legal obligation.</p>
                  <p>
                    Necessary for our legitimate interests (to keep our records
                    updated and to study how customers interact with our prize
                    competitions).
                  </p>
                </td>
              </tr>
              <tr>
                <td>
                  We may use the personal and contact information you have
                  provided to ask you to take part in market research or a
                  survey in order to improve our website and service.
                </td>
                <td>
                  Necessary for our legitimate interests (to ensure that we are
                  able to provide the best service to you).
                </td>
              </tr>
              <tr>
                <td>
                  We may use your personal and contact information, transaction
                  details, and financial information as part of a prize draw or
                  competition (including to enter, take part and pay any prizes
                  due as part of that prize draw or competition).
                </td>
                <td>Performance of a contract with you.</td>
              </tr>
              <tr>
                <td>
                  We use information about your use of our website, together
                  with technical information about your device and (in some
                  circumstances) your personal and contact information, and
                  other relevant information that we receive from you or third
                  parties (including aggregated information that we combine with
                  your personal data), to deliver relevant website content and
                  other direct marketing to you on various channels, including
                  on social media platforms, and to manage the delivery of that
                  content.
                </td>
                <td>
                  <p>
                    Necessary for our legitimate interests (to ensure that you
                    are shown relevant content based on your use of our
                    website).
                  </p>
                  <p>Your consent.</p>
                </td>
              </tr>
              <tr>
                <td>
                  We use information about your use of our website and, in some
                  circumstances, personal and contact information about you, to
                  measure or understand the effectiveness of the advertising we
                  serve to you.
                </td>
                <td>
                  Necessary for our legitimate interests (to determine how
                  effective advertising is in order to improve advertising and
                  its relevance).
                </td>
              </tr>
              <tr>
                <td>
                  We use information about your use of our website and technical
                  information, including about your device or where you are
                  accessing our website from, to optimise our service and to
                  improve our website, games, services, direct marketing,
                  customer relationships, behaviour profiling and experiences.
                </td>
                <td>
                  Necessary for our legitimate interests (to measure the
                  interactions with our website, to define types of customers of
                  DAYMADE prize competitions, how customers interact with the
                  prize competitions, to keep our website updated and relevant,
                  to develop our business and to inform our marketing strategy).
                </td>
              </tr>
              <tr>
                <td>
                  If you win on a DAYMADE prize competition, we may use your
                  personal and contact information to contact you to arrange
                  support.
                </td>
                <td>
                  Necessary for our legitimate interests (to manage our winner
                  services).
                </td>
              </tr>
              <tr>
                <td>
                  We may use your personal and limited contact information in
                  any publicity materials or events, provided that you have
                  given your consent.
                </td>
                <td>Your consent.</td>
              </tr>
              <tr>
                <td>
                  We may use your personal data in marketing and/or media
                  content where you have agreed to and signed a consent form,
                  including in advertisements, customer communications and in
                  any other marketing content, across all media.
                </td>
                <td>
                  Necessary for our legitimate interests (to consider and/or to
                  publish your use of our website).
                </td>
              </tr>
              <tr>
                <td>
                  We may use your personal and contact information, financial
                  information, information about any transaction between you and
                  us, copies of your communications with us (including voice
                  recordings), information about your use of the website, or
                  technical information, in order to undertake analysis for the
                  purposes of identifying and dealing with any fraud or
                  fraudulent activity.
                </td>
                <td>
                  <p>Necessary to comply with a legal obligation.</p>
                  <p>
                    Necessary for our legitimate interests (to ensure that
                    transactions and interactions with us are not fraudulent).
                  </p>
                </td>
              </tr>
              <tr>
                <td>
                  We may use your personal and contact information, financial
                  information, information about any transaction between you and
                  us, copies of your communications with us (including voice
                  recordings), information about your use of the website, or
                  technical information for the purpose of dealing with any
                  request, complaint or query from you.
                </td>
                <td>
                  <p>Necessary to comply with a legal obligation.</p>
                  <p>
                    Necessary for our legitimate interests (to respond to you
                    and to deal with any request you may have).
                  </p>
                </td>
              </tr>
            </tbody>
          </table>
          <p>
            We use your personal data to create aggregated data sets. You are
            not identifiable from that aggregated data and it is not considered
            personal data.
          </p>
          <p>
            However, if we combine or connect aggregated data with your personal
            data so that it can directly or indirectly identify you, we treat
            the combined data as personal data which will be used in accordance
            with this Privacy Policy.
          </p>
          <h4>MARKETING</h4>
          <p>
            We strive to provide you with choices regarding certain personal
            data uses, particularly around marketing and advertising. You will
            be given the choice of whether or not to receive marketing messages
            when you buy your DAYMADE ticket(s).
          </p>
          <p>
            If you win a prize draw, we may publish your first name and the
            first initial of your surname in winner announcements. These may
            appear on our website, in promotional emails, and (with your
            consent) in advertising campaigns. We process this information to
            ensure transparency in our prize draws and to celebrate our winners.
          </p>
          <p>
            You have the right to object to your name being published at any
            time by contacting us at{' '}
            <a href="mailto:hello@daymade.co.uk">hello@daymade.co.uk</a>. If you
            do object, this will not affect your eligibility for the prize.
          </p>
          <h4>OPTING OUT</h4>
          <p>
            You can ask us to stop sending you marketing messages at any time by
            following the opt-out links on any marketing message sent to you.
          </p>
          <p>
            You can also ask us to stop sending you marketing messages by
            logging in to your DAYMADE account and modifying your email
            preferences.
          </p>
          <h4>SHARING YOUR INFORMATION WITH OUR MARKETING PARTNERS</h4>
          <p>
            We may share your data with third parties including our marketing
            partners, advertisers, advertising networks and agencies to provide
            targeted advertising or to exclude you from targeted advertising,
            including Facebook, Google and Snapchat. This list is not
            exhaustive. If you would like a full list of our trusted third party
            marketing providers, please get in touch at lisa@daymade.co.uk.
          </p>
          <p>
            We may also share your data with social media providers, including
            Facebook for custom audiences (for information on how to opt out of
            Facebook custom audiences see the information provided by Facebook
            in its Help Centre (
            <a
              target="_blank"
              href="https://www.facebook.com/help/1415256572060999"
              rel="noreferrer"
            >
              https://www.facebook.com/help/1415256572060999
            </a>
            ).
          </p>
          <p>
            We may share your contact and delivery information with prize
            partners, for the purpose of delivering your prize to you.
          </p>
          <p>
            We require all third parties to respect the security of your
            personal data and to treat it in accordance with the law. We do not
            allow our third-party service providers to use your personal data
            for their own purposes and only permit them to process your personal
            data for specified purposes and in accordance with our instructions.
          </p>
          <h4>CHANGE OF PURPOSE</h4>
          <p>
            We will only use your personal data for the purposes for which we
            collected it, unless we reasonably consider that we need to use it
            for another reason and that reason is compatible with the original
            purpose. If you wish to get an explanation as to how the processing
            for the new purpose is compatible with the original purpose, please
            contact us.
          </p>
          <p>
            If we need to use your personal data for an unrelated purpose, we
            will notify you and we will explain the legal basis which allows us
            to do so.
          </p>
          <p>
            Please note that we may process your personal data without your
            knowledge or consent, in compliance with the above rules, where this
            is required or permitted by law.
          </p>

          <h3>4. COOKIES</h3>
          <p>
            We use cookies and similar technologies to recognise you, your
            browser and your device. We also allow carefully selected third
            parties to use cookies, pixels and ad tags while you are browsing
            our website. These collect information about your browsing habits
            and usage and allow us to show you ads while you are browsing our
            website and other sites on the internet. All of our advertising
            cookies used are described in the cookie list below.
          </p>
          <p>
            You can set your browser to refuse all or some browser cookies, or
            to alert you when websites set or access cookies. If you disable or
            refuse cookies, please note that some parts of this website may
            become inaccessible or not function properly.There are four main
            types of cookies and technology. Here’s how and why we use them:
          </p>
          <h4>Strictly necessary cookies</h4>
          <p>
            These are cookies that are required for the operation of our
            website. Without these cookies, you will not be able to use some
            functionality on our website, such as signing into your account.
          </p>
          <h4>Performance cookies</h4>
          <p>
            These cookies collect information about how you use our website and
            help us improve the way our website works by making it easier for
            you to navigate to and complete various tasks.
          </p>
          <h4>Functionality cookies</h4>
          <p>
            These cookies allow us to identify whether you are logged into your
            DAYMADE account and to remember choices you make and your
            preferences, in order to improve your experience of our website.
            They allow your browser to remember which variant of a page you are
            viewing.
          </p>
          <h4>Advertising cookies and pixels</h4>
          <p>
            Our carefully selected media partners place third party cookies,
            pixels and ad tags onto our website on our behalf. These cookies
            record your visit to our website, the pages you have visited and the
            links you have followed. We use this information to make our
            website, and the content displayed on it, more relevant to you. They
            are also used to limit the number of times you see certain content,
            as well to measure the effectiveness of our digital campaigns. We
            may share this analytics information with third parties for these
            purposes. You can opt out of these cookies and still continue to use
            the website using your browser settings.
          </p>
          <p>
            Below is a list of our main partners that may place cookies, pixels
            or other similar technology on your browser or device when visiting
            our website:
          </p>
          <table cellSpacing="0" cellPadding="0">
            <thead>
              <tr>
                <th>Partner name</th>
                <th>What their cookies are used for</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Google Analytics/GTM</td>
                <td>
                  Google Analytics/Google Tag Manager is a web analytics
                  platform that allows your interactions with the website to be
                  measured. The cookies are used to identify you as an distinct
                  user, but not with personally identifiable information.
                </td>
              </tr>
              <tr>
                <td>Google Ads</td>
                <td>
                  Google Ads allows us to display relevant ads to you on certain
                  pages on the site.
                </td>
              </tr>
              <tr>
                <td>Facebook</td>
                <td>
                  The Facebook cookie allows us to record your actions after you
                  have seen or clicked on an ad in order to measure its
                  effectiveness and to serve you with targeted ads based on your
                  visits to our website.
                </td>
              </tr>
              <tr>
                <td>Snapchat</td>
                <td>
                  Snapchat's cookies allow us to monitor the performance of our
                  ad campaigns.
                </td>
              </tr>
              <tr>
                <td>Sendinblue</td>
                <td>
                  Sendinblue's cookies allow us to monitor the performance of
                  our email campaigns and they also allow us to trigger certain
                  emails (like abandoned cart).
                </td>
              </tr>
              <tr>
                <td>Hotjar</td>
                <td>
                  Hotjar cookies allow us to view anonymous screen recordings of
                  your actions on our website. We use Hotjar recordings to fix
                  website bugs and improve user experience without personally
                  identifying anyone.
                </td>
              </tr>
              <tr>
                <td>Trustpilot</td>
                <td>
                  The Trustpilot widget cookies track three anonymised events
                  related to the widget: impressions, views, and clicks. They
                  don’t track any individual user information.
                </td>
              </tr>
              <tr>
                <td>Affiliate Future</td>
                <td>
                  The Affiliate Future cookie is used if you visit DAYMADE via a
                  website that is part of our Affiliate Future network. We use
                  it to attribute your activity on our website to the relevant
                  affiliate, and to pay commission to the affiliate where
                  applicable.
                </td>
              </tr>
              <tr>
                <td>Awin</td>
                <td>
                  The Awin cookie is used if you visit DAYMADE via a website
                  that is part of our Awin network. We use it to attribute your
                  activity on our website to the relevant affiliate, and to pay
                  commission to the affiliate where applicable.
                </td>
              </tr>
            </tbody>
          </table>

          <h3>5. DISCLOSURES OF YOUR PERSONAL DATA TO THIRD PARTIES</h3>
          <p>
            We sometimes share the data we collect from you with the following
            trusted third parties:
          </p>
          <ul>
            <li>
              Prize fulfilment partners – for the purposes of winner management
              and prize fulfilment, ensuring prizes get delivered/organized.
            </li>
            <li>
              Banks and payment providers – to set up a recurring payments on
              your account, to pay prizes to you and to obtain payment of any
              amounts due to us.
            </li>
            <li>
              Our IT system providers – in order to provide software to set up
              your player account, to communicate with you via webchat and other
              communications providers and to provide our systems to us,
              including for the purposes of hosting, support and software
              licensing.
            </li>
            <li>
              Marketing service providers – to allow us to send email
              communications and notifications.
            </li>
            <li>
              Marketing agencies – for the purposes of providing you with
              relevant advertising or marketing on our website or on social
              media, including for the purposes of undertaking behavioural
              modelling for that purpose.
            </li>
            <li>
              Social media platforms – for the purpose of targeted advertising.
            </li>
            <li>
              Online survey companies and market research agencies – to
              undertake surveys and other market research and to analyse the
              results.
            </li>
            <li>
              Marketing service providers – to allow us to send email
              communications and notifications.
            </li>
            <li>
              Marketing agencies – for the purposes of providing you with
              relevant advertising or marketing on our website or on social
              media, including for the purposes of undertaking behavioural
              modelling for that purpose.
            </li>
            <li>
              Social media platforms – for the purpose of targeted advertising.
            </li>
            <li>
              Online survey companies and market research agencies – to
              undertake surveys and other market research and to analyse the
              results.
            </li>
            <li>
              Digital analytics companies – for the purposes of website
              analytics and reporting in respect of our analytics and marketing.
            </li>
            <li>
              Public relations agencies – for the purposes of managing external
              communications about us and our winners.
            </li>
            <li>
              Professional advisers including lawyers, bankers, auditors and
              insurers – for the purposes of providing consultancy, banking,
              legal, insurance and accounting services.
            </li>
            <li>
              Police or other such regulatory authority – as part of an
              investigation or otherwise for legal or regulatory purposes.
            </li>
            <li>
              Dispute Resolution agencies & regulatory bodies – for the purposes
              of dealing with any complaints.
            </li>
            <li>
              Credit reference agencies – for the purpose of checking the
              details that you have provided us with are correct.
            </li>
            <li>
              Security companies and research institutions – for the purposes of
              security, including prevention and detection methods, for the
              protection of DAYMADE.
            </li>
            <li>
              Auditors – to audit our systems and transactions for the purposes
              of ensuring efficiency, or regulatory or contractual compliance.
            </li>
            <li>
              UK law enforcement agencies and third party security companies –
              for the purposes of ensuring that we comply with the law and have
              adequate security measures in place.
            </li>
            <li>
              We will disclose your personal information to third parties if we
              are under a duty to disclose or share your personal data in order
              to comply with any legal or regulatory obligation or request. We
              do this in order to protect our rights, property or safety or of
              our players, or others. This includes exchanging information with
              other companies and organisations for the purposes of fraud
              protection and credit risk reduction.
            </li>
            <li>
              We require all third parties to respect the security of your
              personal data and to treat it in accordance with the law. We do
              not allow our third party service providers to use your personal
              data for their own purposes and only permit them to process your
              personal data for specific purposes in accordance with our
              instructions and the terms of this Privacy Policy.
            </li>
          </ul>

          <h3>6. INTERNATIONAL TRANSFERS</h3>
          <p>
            We do not transfer your personal data outside the European Economic
            Area (EEA).
          </p>

          <h3>7. DATA SECURITY</h3>
          <p>
            We have put in place appropriate security measures to prevent your
            personal data from being accidentally lost, used or accessed in an
            unauthorised way, altered or disclosed. In addition, we limit access
            to your personal data to those employees, agents, contractors who
            have a business need to know. They will only process your personal
            data on our instructions and they are subject to a duty of
            confidentiality.
          </p>
          <p>
            We have put in place procedures to deal with any suspected personal
            data breach and will notify you and any applicable regulator of a
            breach where we are legally required to do so.
          </p>

          <h3>8. DATA RETENTION</h3>
          <h4>HOW LONG WILL YOU USE MY PERSONAL DATA FOR?</h4>
          <p>
            We will only retain your personal data for as long as reasonably
            necessary to fulfil the purposes we collected it for, including for
            the purposes of satisfying any legal, regulatory, tax, accounting or
            reporting requirements. We may retain your personal data for a
            longer period in the event of a complaint or if we reasonably
            believe there is a prospect of litigation in respect to our
            relationship with you.
          </p>
          <p>
            To determine the appropriate retention period for personal data, we
            consider the amount, nature and sensitivity of the personal data,
            the potential risk of harm from unauthorised use or disclosure of
            your personal data, the purposes for which we process your personal
            data and whether we can achieve those purposes through other means,
            and the applicable legal, regulatory, tax, accounting or other
            requirements.
          </p>
          <h4>WHAT WE MAY NEED FROM YOU</h4>
          <p>
            We may need to request specific information from you to help us
            confirm your identity and ensure your right to access your personal
            data (or to exercise any of your other rights). This is a security
            measure to ensure that personal data is not disclosed to any person
            who has no right to receive it. We may also contact you to ask you
            for further information in relation to your request to speed up our
            response.
          </p>
          <h4>TIME LIMIT TO RESPOND</h4>
          <p>
            We try to respond to all legitimate requests within one month.
            Occasionally it could take us longer than a month if your request is
            particularly complex or you have made a number of requests. In this
            case, we will notify you and keep you updated.
          </p>

          <h3>9. YOUR LEGAL RIGHTS</h3>
          <p>You have the right to:</p>
          <p>
            Request access to your personal data (commonly known as a “data
            subject access request”). This enables you to receive a copy of the
            personal data we hold about you and to check that we are lawfully
            processing it.
          </p>
          <p>
            Request correction of the personal data that we hold about you. This
            enables you to have any incomplete or inaccurate data we hold about
            you corrected, though we may need to verify the accuracy of the new
            data you provide to us.
          </p>
          <p>
            Request erasure of your personal data. This enables you to ask us to
            delete or remove personal data where there is no good reason for us
            continuing to process it. You also have the right to ask us to
            delete or remove your personal data where you have successfully
            exercised your right to object to processing (see below), where we
            may have processed your information unlawfully or where we are
            required to erase your personal data to comply with local law. Note,
            however, that we may not always be able to comply with your request
            of erasure for specific legal reasons which will be notified to you,
            if applicable, at the time of your request.
          </p>
          <p>
            Object to processing of your personal data where we are relying on a
            legitimate interest (or those of a third party) and there is
            something about your particular situation which makes you want to
            object to processing on this ground as you feel it impacts on your
            fundamental rights and freedoms. You also have the right to object
            where we are processing your personal data for direct marketing
            purposes. In some cases, we may demonstrate that we have compelling
            legitimate grounds to process your information which override your
            rights and freedoms.
          </p>
          <p>
            Request restriction of processing of your personal data. This
            enables you to ask us to suspend the processing of your personal
            data in the following scenarios:
          </p>
          <ol>
            <li>If you want us to establish the data’s accuracy.</li>
            <li>
              Where our use of the data is unlawful but you do not want us to
              erase it.
            </li>
            <li>
              Where you need us to hold the data even if we no longer require it
              as you need it to establish, exercise or defend legal claims.
            </li>
            <li>
              You have objected to our use of your data but we need to verify
              whether we have overriding legitimate grounds to use it.
            </li>
          </ol>
          <p>
            Request the transfer of your personal data to you or to a third
            party. We will provide to you, or a third party you have chosen,
            your personal data in a structured, commonly used, machine-readable
            format. Note that this right only applies to automated information
            which you initially provided consent for us to use or where we used
            the information to perform a contract with you.
          </p>
          <p>
            Withdraw consent at any time where we are relying on consent to
            process your personal data. However, this will not affect the
            lawfulness of any processing carried out before you withdraw your
            consent. If you withdraw your consent, we may not be able to provide
            certain products or services to you. We will advise you if this is
            the case at the time you withdraw your consent.
          </p>

          <h3>10. CHANGES TO THIS PRIVACY POLICY</h3>
          <p>
            We keep our privacy policy under regular review. Any changes that we
            may make to our Privacy Policy in the future will be posted on our
            website. Where appropriate, we will notify you of the changes when
            you next visit our website, or via email.
          </p>
          <p>This Privacy Policy was last updated on 2nd June 2023.</p>
          <style jsx>{styles}</style>
        </div>
      </div>

      <GlowBar color="rgba(94, 254, 223, 0.35)" direction="up" height="25px" />
    </MainLayout>
  );
};

const styles = css`
  .container {
    width: 100%;
    padding-top: 20px;
    margin-bottom: 20px;
  }

  p,
  h2,
  h3,
  h4 {
    text-align: left;
  }

  p,
  li,
  td {
    font-weight: 600;
  }

  table {
    border-collapse: collapse;
  }

  td,
  th {
    padding: 8px;
    border: 1px solid #000;
  }

  th {
    font-weight: bold;
  }

  a {
    margin: 0;
  }

  :global(.footer) {
    margin-top: 0 !important;
  }
`;

export default PrivacyPolicy;
